import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'translations/i18n';

import { titleForProject, subtitleForProject } from 'helpers/project-helper';

import { fetchProjects } from 'components/assetFeed/v1api/fetchHelpers';
import styled from 'styled-components';

import { InputCheckbox } from '@companycam/slab-web';
import FilterButtonWithPopover from './shared/FilterButtonWithPopover';
import SearchInput from './shared/SearchInput';
import EmptyState from './shared/EmptyState';
import {
  CheckboxOptionsContainer,
  CheckboxOption,
} from './shared/styledComponents';

import { selectCompanyId } from 'selectors/companySelectors';
import { type Project } from 'data/v1/data-contracts';
import { type DataTestIds } from './types';

const SortedLabel = styled.div`
  color: var(--cc_color_text_nonessential);
  font-size: var(--cc_size_text_s);
  padding: var(--cc_size_spacing_m) 0 var(--cc_size_spacing_s)
    var(--cc_size_spacing_s);
`;

const ProjectName = styled.div`
  font-size: var(--cc_size_text_s);
  color: var(--cc_color_text_default);
  font-weight: 600;
`;

const AddressDiv = styled.div`
  padding-top: var(--cc_size_spacing_xs);
  font-size: var(--cc_size_text_s);
  color: var(--cc_color_text_subtle);
  font-weight: 400;
`;

const projectDisplayValue = (project: Project) => titleForProject(project);

type ProjectFilterProps = {
  activeFilterProjects: Project[];
  onChange: (arg: { projects: Project[] }) => void;
  dataTestIds?: DataTestIds;
  eventTracking?: {
    dropdownClicked: () => void;
    selectionToggled: () => void;
  };
};

const ProjectFilter = ({
  activeFilterProjects,
  onChange,
  dataTestIds,
  eventTracking,
}: ProjectFilterProps) => {
  const [projects, setProjects] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const companyId = useSelector(selectCompanyId);

  const projectsFilterActive = activeFilterProjects.length;
  const selectedProjectIds = activeFilterProjects.map(({ node_id }) => node_id);

  const [queryTerm, setQueryTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchProjects(companyId, queryTerm).then((options: Project[]) => {
        setProjects(options);
        setIsLoading(false);
      });
    }
  }, [companyId, isOpen, queryTerm]);

  const handleToggle = () => {
    if (!isOpen) {
      eventTracking?.dropdownClicked();
    }

    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const addOrRemove = (project: Project) =>
    selectedProjectIds.includes(project.node_id)
      ? activeFilterProjects.filter(
          ({ node_id }) => node_id !== project.node_id,
        )
      : [...activeFilterProjects, project];

  const handleChange = (project: Project) => {
    const nextFilterProjects = addOrRemove(project);
    onChange({ projects: nextFilterProjects });
    eventTracking?.selectionToggled();
  };

  type ProjectQueryTerm = {
    target: {
      value: string;
    };
  };
  const handleQueryChange = ({ target: { value } }: ProjectQueryTerm) =>
    setQueryTerm(value);

  const handleClear = () => {
    onChange({ projects: [] });
    setIsOpen(false);
    setQueryTerm('');
  };

  const emptyStateMessage = isLoading
    ? translate('common.loading')
    : translate('filters.noResultsFound', {
        filterType: translate('common.projects'),
      });

  return (
    <FilterButtonWithPopover
      id="filter-row-projects-popover"
      isOpen={isOpen}
      onOpen={handleToggle}
      isSelected={!!projectsFilterActive}
      selectedCount={projectsFilterActive}
      title={translate('common.projects')}
      onClearClick={handleClear}
      data-testid={dataTestIds.filterButton}
      clearButtonDataTestId={dataTestIds.clearButton}
    >
      {projects.length || queryTerm !== '' ? (
        <SearchInput
          id="filter-row-projects-input"
          label={translate('filters.projectQueryTerm')}
          placeholder={translate('common.search')}
          value={queryTerm}
          onChange={handleQueryChange}
          data-testid={dataTestIds.searchInput}
        />
      ) : null}

      {!projects.length ? <EmptyState message={emptyStateMessage} /> : null}

      {!!projects.length && (
        <SortedLabel>
          {queryTerm || !projects.length
            ? null
            : translate('assetFeed.filterRow.sortedByLabel')}
        </SortedLabel>
      )}
      <CheckboxOptionsContainer>
        {projects.map((option) => {
          const selected = selectedProjectIds.includes(option.node_id);
          const optionLabel = (
            <>
              <ProjectName>{projectDisplayValue(option)}</ProjectName>
              <AddressDiv>{subtitleForProject(option)}</AddressDiv>
            </>
          );
          return (
            <CheckboxOption key={option.node_id}>
              <InputCheckbox
                id={`projects-${option.node_id}`}
                label={optionLabel}
                value={option.node_id}
                onChange={() => handleChange(option)}
                checked={selected}
              />
            </CheckboxOption>
          );
        })}
      </CheckboxOptionsContainer>
    </FilterButtonWithPopover>
  );
};

export default ProjectFilter;
